// src/api/setupAxios.js
// Configura interceptors GLOBALES de axios para:
//  1) añadir Authorization: Bearer <access_token> a cada petición
//  2) renovar automáticamente el access_token si llega un 401, usando el refresh_token
//  3) renovar el access_token PROACTIVAMENTE cada 10 min (antes de que caduque),
//     para minimizar los 401 en mitad de una acción del usuario.
//
// Importarlo una sola vez en index.js antes de renderizar la app.
import axios from "axios";
import {
  beginRetrying, markFailed, endAttempt, registerRetry, unregisterRetry,
} from "./connectionOverlay";

let isRefreshing = false;
let pendingQueue = [];

// Ninguna petición debería tardar más de esto: sin timeout (por defecto axios
// espera indefinidamente), una conexión muy lenta deja al usuario con un
// spinner infinito y sin ningún aviso. 20s da margen de sobra a peticiones
// grandes (p.ej. /productos, ~460KB) en conexiones móviles lentas: un timeout
// demasiado corto las corta antes de tiempo y dispara avisos de conexión
// para peticiones que en realidad habrían acabado bien.
axios.defaults.timeout = 20000;

// Reintentos automáticos ante fallos de red/timeout:
//  - SILENT_RETRIES: reintento(s) SIN mostrar nada en pantalla. Cubre el caso
//    típico de "acabo de entrar en la app" (arranque en frío del backend/BBDD,
//    handshake TLS lento, etc.), que no es una desconexión real: no tiene
//    sentido asustar al usuario con un error a la primera de cambio.
//  - VISIBLE_RETRIES: si el problema persiste más allá de eso, ahí sí se avisa
//    (overlay "reintentando"). Si también falla, se pide reintentar a mano.
const SILENT_RETRIES = 1;
const VISIBLE_RETRIES = 1;
const RETRY_DELAY_MS = 1200;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

function isNetworkOrTimeoutError(error) {
  // Sin response = no hubo respuesta HTTP (timeout, DNS, sin red, CORS, etc.),
  // a diferencia de un 4xx/5xx normal que sí respondió.
  return !error.response;
}

// Intenta la petición `n` veces más (con pausa entre cada una), en silencio.
// Devuelve la respuesta si alguna tiene éxito; si se agotan los intentos,
// re-lanza el último error de red/timeout (o cualquier otro error tal cual).
async function reintentarEnSilencio(config, n) {
  let lastErr;
  for (let intento = 0; intento < n; intento++) {
    await sleep(RETRY_DELAY_MS);
    try {
      return await axios(config);
    } catch (err) {
      if (!isNetworkOrTimeoutError(err)) throw err;
      lastErr = err;
    }
  }
  throw lastErr;
}

async function reintentarConexion(config) {
  try {
    return await reintentarEnSilencio(config, SILENT_RETRIES);
  } catch (err) {
    if (!isNetworkOrTimeoutError(err)) throw err;
  }

  // Sigue sin ir tras el margen inicial: ahora sí molestamos al usuario.
  beginRetrying();
  try {
    try {
      return await reintentarEnSilencio(config, VISIBLE_RETRIES);
    } catch (err) {
      if (!isNetworkOrTimeoutError(err)) throw err;
    }

    // Se agotaron los reintentos automáticos: pedir al usuario que reintente a mano.
    markFailed();
    return await new Promise((resolve, reject) => {
      const intentarDeNuevo = async () => {
        try {
          const res = await axios(config);
          unregisterRetry(intentarDeNuevo);
          resolve(res);
        } catch (err) {
          if (!isNetworkOrTimeoutError(err)) {
            unregisterRetry(intentarDeNuevo);
            reject(err);
          }
          // sigue sin conexión: el overlay sigue visible con el botón, se
          // queda registrado a la espera de otro click.
        }
      };
      registerRetry(intentarDeNuevo);
    });
  } finally {
    endAttempt();
  }
}

// Mensaje amable cuando la sesión no se ha podido renovar (token/refresh caducados
// o sin red en ese instante): mejor esto que un error críptico o un alert genérico.
export const MENSAJE_ERROR_SESION = "⚠️ Error de sesión. Vuelve a intentarlo en unos segundos.";

// Cuando el backend corta la sesión porque el usuario fue dado de baja (ver
// before_request en app.py: comprueba `activo` en cada petición, no solo al
// hacer login/refresh), esto es lo que se muestra antes de mandar a /login.
export const MENSAJE_USUARIO_INACTIVO = "⚠️ Tu usuario ha sido dado de baja. Contacta con tu supervisor si crees que es un error.";

function flushQueue(error, token = null) {
  pendingQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  pendingQueue = [];
}

function clearSession() {
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("user");
}

// Llama a /login/refresh y guarda los tokens nuevos. Devuelve el access_token nuevo.
// Reintenta en silencio ante fallos de red/timeout (un corte de conexión puntual
// NO significa que el refresh_token sea inválido); solo se rinde de inmediato si
// el servidor responde de verdad rechazando el refresh (401/403 = sesión realmente
// caducada). Esta distinción es crítica: confundirla con un simple corte de red
// es lo que antes provocaba un logout forzado a mitad de sesión.
async function refrescarTokens(refreshToken) {
  const config = {
    method: "post",
    url: "/api/login/refresh",
    headers: { Authorization: `Bearer ${refreshToken}` },
  };
  let lastErr;
  for (let intento = 0; intento <= SILENT_RETRIES; intento++) {
    if (intento > 0) await sleep(RETRY_DELAY_MS);
    try {
      const res = await axios(config);
      const newAccess = res.data.access_token;
      const newRefresh = res.data.refresh_token;
      if (newAccess) localStorage.setItem("access_token", newAccess);
      if (newRefresh) localStorage.setItem("refresh_token", newRefresh);
      return newAccess;
    } catch (err) {
      lastErr = err;
      if (!isNetworkOrTimeoutError(err)) throw err; // rechazo real del servidor: no reintentar
    }
  }
  throw lastErr;
}

axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token");
  if (token) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config || {};
    const status = error.response?.status;

    // No reintentar el propio /refresh ni el /login
    const url = originalRequest.url || "";
    const isAuthEndpoint = url.includes("/login/refresh") || url.endsWith("/login/") || url.endsWith("/login");

    // Sin respuesta HTTP (timeout, sin red, DNS...): reintento automático +
    // overlay, en vez de dejar que el error genérico llegue tal cual al
    // catch de cada pantalla (ver connectionOverlay.js / ConnectionOverlay.js).
    if (isNetworkOrTimeoutError(error) && !isAuthEndpoint && !originalRequest._connRetried) {
      originalRequest._connRetried = true;
      try {
        return await reintentarConexion(originalRequest);
      } catch (err) {
        return Promise.reject(err);
      }
    }

    // 403 con error='user_inactive': el usuario fue dado de baja después de emitir
    // el token, y el backend lo corta en CADA petición (antes ni siquiera hacía falta
    // que el access_token caducase). No hay refresh que arregle esto: se cierra la
    // sesión ya, igual que cuando el refresh_token es rechazado de verdad.
    if (status === 403 && error.response?.data?.error === "user_inactive") {
      clearSession();
      alert(MENSAJE_USUARIO_INACTIVO);
      if (window.location.pathname !== "/login") window.location.assign("/login");
      return Promise.reject(error);
    }

    if (status !== 401 || originalRequest._retry || isAuthEndpoint) {
      return Promise.reject(error);
    }

    const refreshToken = localStorage.getItem("refresh_token");
    if (!refreshToken) {
      clearSession();
      alert(MENSAJE_ERROR_SESION);
      if (window.location.pathname !== "/login") window.location.assign("/login");
      return Promise.reject(error);
    }

    if (isRefreshing) {
      // Esperar a que termine el refresh en curso
      return new Promise((resolve, reject) => {
        pendingQueue.push({ resolve, reject });
      })
        .then((token) => {
          originalRequest._retry = true;
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return axios(originalRequest);
        });
    }

    originalRequest._retry = true;
    isRefreshing = true;
    try {
      const newAccess = await refrescarTokens(refreshToken);
      flushQueue(null, newAccess);
      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers.Authorization = `Bearer ${newAccess}`;
      return axios(originalRequest);
    } catch (err) {
      flushQueue(err, null);
      // Solo cerramos sesión si el servidor RECHAZÓ el refresh de verdad (401/403:
      // refresh_token realmente caducado/inválido). Un fallo de red/timeout tras
      // agotar los reintentos de refrescarTokens() NO es eso: es solo que ahora
      // mismo no hay conexión, y forzar logout aquí borraría trabajo en curso del
      // usuario (p.ej. un carrito de compra a medio hacer) por un simple corte
      // puntual. En ese caso dejamos la sesión intacta y solo falla esta acción.
      if (!isNetworkOrTimeoutError(err)) {
        clearSession();
        alert(MENSAJE_ERROR_SESION);
        if (window.location.pathname !== "/login") window.location.assign("/login");
      }
      return Promise.reject(err);
    } finally {
      isRefreshing = false;
    }
  }
);

// Refresco proactivo: cada 50 min, si hay sesión iniciada, renueva el access_token
// ANTES de que caduque (dura 1h), para que la mayoría de peticiones nunca lleguen
// a toparse con un 401 en mitad de una acción del usuario. Es un complemento al
// refresco reactivo de arriba, no un sustituto (sigue haciendo falta para cuando
// la app estuvo en segundo plano/suspendida más de 1h, sin red en ese momento, etc.).
const INTERVALO_REFRESCO_PROACTIVO_MS = 50 * 60 * 1000;
setInterval(() => {
  const refreshToken = localStorage.getItem("refresh_token");
  if (!refreshToken || isRefreshing) return;
  refrescarTokens(refreshToken).catch(() => {
    // Si falla, no forzamos logout aquí: el flujo reactivo de arriba se encargará
    // en cuanto la próxima petición real reciba un 401.
  });
}, INTERVALO_REFRESCO_PROACTIVO_MS);

// Wrapper de window.fetch que añade Authorization: Bearer <access_token>.
// Usar en lugar de fetch() para llamadas a endpoints del backend protegidos por JWT.
// No implementa el flujo de refresh: si recibe 401 simplemente devuelve la respuesta;
// para refresh usa axios (que sí tiene interceptor).
export async function apiFetch(input, init = {}) {
  const headers = new Headers(init.headers || {});
  const token = localStorage.getItem("access_token");
  if (token && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  return fetch(input, { ...init, headers });
}

export default axios;
